import { formatDate } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { FiltroGenericoComponent } from 'src/app/components/filtro-generico/filtro-generico.component';
import { Inject } from '@angular/core';
import { ENVIRONMENT } from 'src/app/constants';
import { Environment } from 'src/app/types';
import { downloadArquivoDeBase64 } from 'src/app/helper';
import { IEquipamento, ISite } from 'src/app/interfaces';
import { ListaPaginada } from 'src/app/models';
import {
  AuthenticationService,
  EquipamentoService,
  SiteService
} from 'src/app/services';

@Component({
  selector: 'app-listar-equipamentos',
  templateUrl: './listar-equipamentos.component.html',
  styleUrls: ['./listar-equipamentos.component.scss']
})
export class ListarEquipamentosComponent implements OnInit, OnDestroy {
  private subscriptions$ = new Subscription();

  equipamentos = new ListaPaginada<IEquipamento>();
  filtraForm!: FormGroup;

  equipamentoPDFBase64!: string;
  equipamentoCSVBase64!: string;

  token = this.authenticationService.obterToken();
  podeGerenciarQRCode: boolean =
    this.token?.autorizacoes?.gerenciarQrCode || false;
  cabecalhosTabela: string[] = [];

  @ViewChild(FiltroGenericoComponent)
  filtroGenerico!: FiltroGenericoComponent;

  @ViewChild('printFrame') printFrame: ElementRef;

  sitesOptions: ISite[] = [];
  equipamentoAtivo = true;

  filterData?: {
    site: string;
    patrimonio: string;
    contrato: string;
    departamento: string;
    numserie: string;
    local: string;
  };

  isGerandoQRCode: string | null = null;
  qrCodeModalVisible = false;
  qrCodeModalImage = '';
  equipamentoSelecionado: IEquipamento | null = null;
  modalQRCodeUrl = '';
  dataAtual = new Date();

  constructor(
    private fb: FormBuilder,
    private equipamentoService: EquipamentoService,
    private siteService: SiteService,
    private authenticationService: AuthenticationService,
    private router: Router,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {
    this.configurarCabecalhosTabela();
  }

  private configurarCabecalhosTabela(): void {
    const cabecalhosBase = [
      'Cliente',
      'Site',
      'Localização',
      'Nº do Patrimônio',
      'Contrato',
      'Status',
      'Marca',
      'Modelo',
      'Nº de Série',
      'Suporte',
      'Ações'
    ];

    if (this.podeGerenciarQRCode) {
      cabecalhosBase.push('QR Code');
    }

    this.cabecalhosTabela = cabecalhosBase;
  }

  exibirSite(site: ISite) {
    return site.nome;
  }

  valorSite(site: ISite) {
    return site.codSite;
  }

  receiveFilterData(filterData: any) {
    this.filterData = filterData;
  }

  getEquipamentos(reiniciarPaginacao?: boolean) {
    if (reiniciarPaginacao) {
      this.equipamentos.paginaAtual = 1;
    }

    this.subscriptions$.add(
      this.equipamentoService
        .getEquipamentos({
          idUsuario: this.token.id,
          CodSite: this.filterData?.site ?? '',
          patrimonio: this.filterData?.patrimonio ?? '',
          codContrato: this.filterData?.contrato
            ? [this.filterData.contrato]
            : this.token.codContrato,
          contratoAtivo: this.filtraForm.value.contratoAtivo,
          departamento: this.filterData?.departamento ?? '',
          numserie: this.filterData?.numserie ?? '',
          local: this.filterData?.local ?? '',
          equipamentoAtivo: this.equipamentoAtivo,
          itensPorPagina: this.equipamentos.itensPorPagina,
          paginaAtual: this.equipamentos.paginaAtual,
          TipoUsuario: this.token.tipoUsuario,
          CodGrupoEconomico: this.token.codGrupoEconomico,
          pesquisar: this.filtraForm.value.pesquisar
        })
        .subscribe(({ data }) => {
          this.equipamentos = data;
          this.filtroGenerico?.hidePopUp();
        })
    );
  }

  estaAtivoOuInativo() {
    this.equipamentoAtivo = !this.equipamentoAtivo;
    this.getEquipamentos(true);
  }

  getSite() {
    this.subscriptions$.add(
      this.siteService
        .getSite({
          paginar: false,
          TipoUsuario: this.token.tipoUsuario,
          codContrato: this.token.codContrato,
          CodGrupoEconomico: this.token.codGrupoEconomico
        })
        .subscribe(({ data }) => {
          this.sitesOptions = data.registros;
        })
    );
  }

  getDownloadPDFEquipamentos() {
    this.subscriptions$.add(
      this.equipamentoService
        .getDownloadPDFFechamento({
          idUsuario: this.token.id,
          CodSite: this.filterData?.site ?? '',
          patrimonio: this.filterData?.patrimonio ?? '',
          codContrato: this.filterData?.contrato
            ? [this.filterData.contrato]
            : this.token.codContrato,
          departamento: this.filterData?.departamento ?? '',
          contratoAtivo: this.filtraForm.value.contratoAtivo,
          equipamentoAtivo: this.equipamentoAtivo,
          CodGrupoEconomico: this.token.codGrupoEconomico,
          paginar: false
        })
        .subscribe(({ data }) => {
          downloadArquivoDeBase64({
            base64: data,
            tipo: 'application/pdf',
            nomeArquivo: `REGISTROS_EQUIPAMENTOS-${formatDate(
              new Date(),
              'dd-MM-yyyy',
              'pt-BR'
            )}`
          });
        })
    );
  }

  getDownloadCSVEquipamentos() {
    this.subscriptions$.add(
      this.equipamentoService
        .getDownloadCSVFechamento({
          idUsuario: this.token.id,
          CodSite: this.filterData?.site ?? '',
          patrimonio: this.filterData?.patrimonio ?? '',
          codContrato: this.filterData?.contrato
            ? [this.filterData.contrato]
            : this.token.codContrato,
          departamento: this.filterData?.departamento ?? '',
          contratoAtivo: this.filtraForm.value.contratoAtivo,
          equipamentoAtivo: this.equipamentoAtivo,
          CodGrupoEconomico: this.token.codGrupoEconomico,
          paginar: false
        })
        .subscribe(({ data }) => {
          downloadArquivoDeBase64({
            base64: data,
            tipo: 'text/csv',
            nomeArquivo: `REGISTROS_EQUIPAMENTOS-${formatDate(
              new Date(),
              'dd-MM-yyyy',
              'pt-BR'
            )}`
          });
        })
    );
  }

  gerarQRCode(netName: string): void {
    this.isGerandoQRCode = netName;

    this.subscriptions$.add(
      this.equipamentoService.gerarTokenEquipamento(netName).subscribe({
        next: ({ data }) => {
          const token = data.token;

          const url = `https://portal.kmdobrasil.com.br/login?token=${token}`;
          this.modalQRCodeUrl = url;

          this.qrCodeModalImage = `https://api.qrserver.com/v1/create-qr-code/?size=450x260&data=${encodeURIComponent(
            url
          )}`;

          this.equipamentoSelecionado =
            this.equipamentos.registros.find((e) => e.netName === netName) ||
            null;

          this.dataAtual = new Date();
          this.qrCodeModalVisible = true;
          this.isGerandoQRCode = null;
        },
        error: () => {
          this.isGerandoQRCode = null;
        }
      })
    );
  }

  fecharModal() {
    this.qrCodeModalVisible = false;
    this.equipamentoSelecionado = null;
    this.qrCodeModalImage = '';
    this.modalQRCodeUrl = '';
  }

  copiarParaClipboard() {
    if (this.modalQRCodeUrl) {
      navigator.clipboard.writeText(this.modalQRCodeUrl);
    }
  }

  /**
   * Retorna a classe CSS apropriada para o tamanho do número de série no QR Code
   * @param numSerie - Número de série do equipamento
   * @returns Nome da classe CSS
   */
  getClasseTamanhoSerial(numSerie: string | null | undefined): string {
    if (!numSerie) {
      return 'qr-serial';
    }

    const tamanho = numSerie.replace(/\s/g, '').length;

    if (tamanho > 18) {
      return 'qr-serial-small';
    } else if (tamanho > 10 && tamanho <= 18) {
      return 'qr-serial-medium';
    } else {
      return 'qr-serial';
    }
  }

  imprimirQRCode() {
    const classeSerial = this.getClasseTamanhoSerial(
      this.equipamentoSelecionado?.numSerie || ''
    );

    const printContent = `
  <!DOCTYPE html>
<html>
  <head>
    <title>QR Code - ${
      this.equipamentoSelecionado?.numSerie || 'Equipamento'
    }</title>
    <style>
      @page {
        size: 90mm 50mm !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      body {
        margin: 0 !important;
        padding: 0 !important;
        width: 90mm !important;
        height: 50mm !important;
        background: white !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        font-family: "Arial Narrow", Arial, sans-serif !important;
      }
     
      .qr-template {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: row;        
        box-sizing: border-box;
        background: #fff;
        gap: 1mm;
        margin: 1mm;
      }

      .qr-info {
        width: 50%;
        display: flex;
        flex-direction: column;
        text-align: center;
        justify-content: space-evenly;
      }

      .qr-logo {
        width: 45mm;
        margin-top: 0.5mm;
      }

      .qr-site {
        font-size: 14px !important;
        font-weight: 200 !important;
        margin: 0.5mm 0;
      }

      .serial-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4mm;
        align-items: center;
        margin-bottom: 1mm;
      }

      .qr-label {
        font-size: 22px !important;
        font-weight: 600 !important;
        margin: 0;
        line-height: 1;
      }

      .qr-serial {
        font-size: 20px !important;
        font-weight: 600 !important;
        margin: 0.3mm 0 0 0;
        line-height: 1;
        letter-spacing: 0.5px;
      }

      .qr-serial-medium {
        font-size: 16px !important;
        font-weight: 600 !important;
        margin: 0.3mm 0 0 0;
        line-height: 1.2;
        letter-spacing: 0.5px;
        word-break: break-word;
      }

      .qr-serial-small {
        font-size: 12px !important;
        font-weight: 600 !important;
        margin: 0.3mm 0 0 0;
        line-height: 1.3;
        letter-spacing: 0.3px;
        word-break: break-word;
      }

      .qr-code-area {
        width: 50%;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
      }

      .qr-code-img {
        width: 100% !important; 
        height: auto !important;
        max-height: 34mm;
        object-fit: contain;
        background: white;
        border: none;
      }
          
      .qr-scan-text {
        font-size: 12px !important; 
        font-weight: 200 !important;
        text-align: center;
        line-height: 1;
      }
          
      @media print {
        body {
          width: 90mm !important;
          height: 50mm !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .qr-template {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      }
    </style>
  </head>
  <body>
    <div class="qr-template">
      <div class="qr-info">
        <img src="${
          window.location.origin
        }/assets/images/kmDoBrasil/logo/logoQrCode.png" class="qr-logo" />
        <p class="qr-site">www.kmdobrasil.com.br</p>
        <div class="serial-wrapper">
          <p class="qr-label">Nº. de Série:</p>
          <p class="${classeSerial}">${
      this.equipamentoSelecionado?.numSerie
    }</p>
        </div>
      </div>
      <div class="qr-code-area">
        <img src="${this.qrCodeModalImage}" class="qr-code-img" />
        <p class="qr-scan-text">Escaneie para Suporte</p>
      </div>
    </div>
  </body>
</html>
  `;

    const iframe = this.printFrame.nativeElement;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;

    if (doc) {
      doc.open();
      doc.write(printContent);
      doc.close();

      const checkImageLoad = () => {
        const img = doc.querySelector('img.qr-code-img');
        if (img && img.complete && img.naturalHeight !== 0) {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } else {
          setTimeout(checkImageLoad, 100);
        }
      };

      setTimeout(checkImageLoad, 100);
    }
  }

  private formatarData(data: Date): string {
    const meses = [
      'janeiro',
      'fevereiro',
      'março',
      'abril',
      'maio',
      'junho',
      'julho',
      'agosto',
      'setembro',
      'outubro',
      'novembro',
      'dezembro'
    ];

    const mes = meses[data.getMonth()];
    const ano = data.getFullYear();

    return `${mes} / ${ano}`;
  }

  ngOnInit() {
    this.filtraForm = this.fb.group({
      contratoAtivo: [true],
      pesquisar: ['']
    });

    this.getEquipamentos();
    this.getSite();
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}
